import { useFlatlistProps } from '@/utils'
import React, { useEffect, useCallback, CSSProperties, useRef } from 'react'
import { ActivityIndicator, View, Text } from '@/components'

type SimpleListProps<T extends {}> = Partial<
  Omit<ReturnType<typeof useFlatlistProps>, 'keyExtractor' | 'placeholder'>
> & {
  renderItem: (props: { item: T; index: number }) => JSX.Element
  keyExtractor: (item: T, index: number) => string | number
  header?: React.ReactNode
  footer?: React.ReactNode
  style?: CSSProperties
  placeholder?: {
    title?: string
    description?: string
    loading?: boolean
  }
}

export const SimpleList = <T extends {}>(props: SimpleListProps<T>) => {
  const {
    data,
    fetchNextPage,
    loading,
    hasNextPage,
    renderItem: providedRenderItem,
    keyExtractor,
    header,
    footer,
    style,
    placeholder = {},
  } = props

  const elementRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    const element = elementRef.current

    if (!element) return

    const { scrollTop, scrollHeight, clientHeight } = element

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      if (!loading && hasNextPage) {
        fetchNextPage()
      }
    }
  }, [fetchNextPage, loading, hasNextPage])

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    element.addEventListener('scroll', handleScroll)
    return () => element.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const renderItem = useCallback(
    (item: T, index: number) => {
      return providedRenderItem({ item, index })
    },
    [providedRenderItem],
  )

  const isEmpty = data?.length <= 0 || !data

  if (loading && isEmpty) {
    return (
      <View style={style} variants={['center']}>
        <ActivityIndicator debugName='list:loading' />
      </View>
    )
  }

  if (isEmpty) {
    return (
      <View style={style} variants={['center']}>
        <View variants={['center', 'column', 'gap:1']}>
          <Text text={placeholder?.title ?? 'No items found'} variants={['h4']} />
          <Text text={placeholder?.description ?? `Oops! Looks like it's not here at the moment.`} variants={['p1']} />
        </View>
      </View>
    )
  }

  return (
    <View ref={elementRef} style={style}>
      {header}

      {data?.map((item, index) => (
        <React.Fragment key={keyExtractor(item as unknown as T, index)}>
          {renderItem(item as unknown as T, index)}
        </React.Fragment>
      ))}

      {loading && hasNextPage ? (
        <View variants={['center', 'fullWidth']}>
          <ActivityIndicator debugName='list:loading' />
        </View>
      ) : null}

      {footer}
    </View>
  )
}
